import { useEffect, useState } from 'react';

/**
 * "AI Rob, in Rob's voice": whether answers are read aloud (BRAND_SPEC §11.1
 * voice mode). One setting for the whole app, switched in the menu and kept
 * per browser (apr-voice-replies), so every conversation surface agrees.
 *
 * Listeners run synchronously inside the tap that changed it: iOS only lets
 * audio start inside a user gesture, so a chat surface primes its player
 * right there (ChatView).
 *
 * Campaign landings start with it off, whatever was saved (§11.1), without
 * overwriting the saved choice; a person can still turn it on.
 */
const KEY = 'apr-voice-replies';
const CHOSEN_KEY = 'apr-voice-chosen'; // this tab: the person switched it themselves
let value = null;
const listeners = new Set();

function readSaved() {
  try { return localStorage.getItem(KEY) !== '0'; } catch (e) { return true; }
}

export function getVoiceReplies() {
  if (value === null) value = readSaved();
  return value;
}

export function setVoiceReplies(next) {
  value = Boolean(next);
  try { localStorage.setItem(KEY, value ? '1' : '0'); } catch (e) { /* storage blocked: this visit only */ }
  try { sessionStorage.setItem(CHOSEN_KEY, '1'); } catch (e) { /* storage blocked */ }
  listeners.forEach((fn) => fn(value));
}

export function toggleVoiceReplies() {
  setVoiceReplies(!getVoiceReplies());
}

/**
 * Why an answer is being read aloud, for the counts (utils/voice.js): 'optin' when the person
 * switched Read aloud on themselves (the saved choice is "1"), 'auto' when it is on because it
 * starts on (never set). A Listen press is 'listen', decided where it is pressed.
 */
export function voiceSrc() {
  try { return localStorage.getItem(KEY) === '1' ? 'optin' : 'auto'; } catch (e) { return 'auto'; }
}

/**
 * A tab that arrived from an ad: off, unless the person has switched it on
 * in this tab themselves. The saved choice is untouched.
 */
export function silenceVoiceRepliesForCampaign() {
  try { if (sessionStorage.getItem(CHOSEN_KEY) === '1') return; } catch (e) { /* storage blocked */ }
  value = false;
  listeners.forEach((fn) => fn(value));
}

export function subscribeVoiceReplies(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useVoiceReplies() {
  const [on, setOn] = useState(getVoiceReplies);
  useEffect(() => {
    setOn(getVoiceReplies());
    return subscribeVoiceReplies(setOn);
  }, []);
  return on;
}
